import React, { useEffect, useRef, useState, useCallback } from 'react'
import MessageBubble from './MessageBubble'
import TypingIndicator from './TypingIndicator'
import ChatInput from './ChatInput'
import WelcomeScreen from './WelcomeScreen'
import { messageApi } from '../api'
import { AlertCircle, RefreshCw } from 'lucide-react'

export default function ChatWindow({
  userInfo,
  conversation,
  onFirstMessage,
}) {
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [error, setError] = useState(null)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const bottomRef = useRef(null)
  const prevConvId = useRef(null)

  const [loadingConvId, setLoadingConvId] = useState(null)
  const isLoading = loadingConvId === conversation?.id

  const currentConvId = useRef(null)
  currentConvId.current = conversation?.id ?? null

  const [awaitingReply, setAwaitingReply] = useState(false)

  // Load messages when conversation changes
  useEffect(() => {
    if (!conversation) {
      setMessages([])
      setAwaitingReply(false)
      return
    }
    if (prevConvId.current === conversation.id) return
    prevConvId.current = conversation.id

    setMessages([])
    setError(null)
    setLoadingMessages(true)

    messageApi.list(conversation.id)
      .then(msgs => {
        setMessages(msgs)
        const waiting = msgs.length > 0 && msgs[msgs.length - 1].role === 'user'
        if (!waiting) sessionStorage.removeItem('pendingConvId')
        setAwaitingReply(waiting)
      })
      .catch(() => setError('Failed to load messages'))
      .finally(() => setLoadingMessages(false))
  }, [conversation?.id])

  // Poll while waiting for a reply that was interrupted by a reload
  useEffect(() => {
    if (!conversation?.id || !awaitingReply) return

    let cancelled = false
    let tries = 0
    const convId = conversation.id

    const timer = setInterval(async () => {
      tries++
      try {
        const msgs = await messageApi.list(convId)
        if (cancelled) return
        if (msgs.length && msgs[msgs.length - 1].role !== 'user') {
          sessionStorage.removeItem('pendingConvId')
          setMessages(msgs)
          setAwaitingReply(false)
          return
        }
      } catch {
        /* ignore, retry on next tick */
      }
      if (!cancelled && tries >= 900) {   // ~30 minutes, then give up
        setAwaitingReply(false)
        setError('The reply is taking too long. Try reloading or resending.')
      }
    }, 2000)

    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [conversation?.id, awaitingReply])

  // Auto-scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });  
  }, [messages, isLoading])

  const sendMessage = useCallback(async (content) => {
    const text = (content || input).trim()
    if (!text || isLoading || awaitingReply) return

    setInput('')
    setError(null)

    // Optimistic user message
    const tempUserMsg = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: text,
      createdAt: new Date().toISOString(),
    }

    let convId = conversation?.id

    // If no conversation, create one first
    if (!convId) {
      try {
        const newConv = await onFirstMessage(text)
        convId = newConv.id
      } catch (err) {
        setError('Failed to create conversation')
        return
      }
    }

    sessionStorage.setItem('pendingConvId', convId)

    setMessages(prev => [...prev, tempUserMsg])
    setLoadingConvId(convId)

    try {
      const response = await messageApi.send(convId, text)
      if (currentConvId.current === convId) {          // still on this conversation
        setMessages(prev => {
          const base = prev.filter(m => m.id !== tempUserMsg.id)
          const ids = new Set(base.map(m => m.id))
          const add = [response.userMessage, response.assistantMessage]
            .filter(m => !ids.has(m.id))               // avoid duplicates after a reload/poll
          return [...base, ...add]
        })
        setAwaitingReply(false)
      }
    } catch (err) {
      if (currentConvId.current === convId) {
        setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id))
        setError('Failed to send message. Is the backend running?')
      }
    } finally {
      sessionStorage.removeItem('pendingConvId')
      setLoadingConvId(id => (id === convId ? null : id))
    }
  }, [input, isLoading, conversation, onFirstMessage])

  const handleSuggestion = (text) => {
    setInput(text)
    // Small delay to let state update then send
    setTimeout(() => sendMessage(text), 50)
  }

  const showWelcome = !conversation && messages.length === 0

  return (
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      background: 'var(--bg-main)',
    }}>
      {/* Header */}
      {conversation && (
        <div style={{
          padding: '14px 24px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(33,33,33,0.85)',
          backdropFilter: 'blur(8px)',
          flexShrink: 0,
        }}>
          <h2 style={{
            fontSize: 14,
            fontWeight: 500,
            color: 'var(--text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 500,
          }}>
            {conversation.title}
          </h2>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0 }}>
            {messages.length} message{messages.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Messages area */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: showWelcome ? '0' : '0 24px',
      }}>
        {showWelcome && (
          <WelcomeScreen
            userInfo={userInfo}
            onSuggestion={handleSuggestion}
            onCreate={() => onFirstMessage('')}
          />
        )}

        {!showWelcome && (
          <div style={{ maxWidth: 1920, margin: '0 auto' }}>
            {loadingMessages ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '60px 0',
              }}>
                <div style={{
                  width: 24,
                  height: 24,
                  border: '2px solid var(--border)',
                  borderTopColor: 'var(--accent)',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }} />
              </div>
            ) : (
              <>
                {messages.map((msg) => (
                  <MessageBubble key={msg.id} message={msg} />
                ))}
                {(isLoading || awaitingReply) && <TypingIndicator />}
              </>
            )}

            {/* Error banner */}
            {error && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.25)',
                borderRadius: '10px',
                padding: '12px 16px',
                marginBottom: '16px',
                color: '#ef4444',
                fontSize: 13,
                animation: 'fadeInUp 0.2s ease',
              }}>
                <AlertCircle size={16} />
                <span style={{ flex: 1 }}>{error}</span>
                <button
                  onClick={() => setError(null)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#ef4444',
                    cursor: 'pointer',
                    padding: '2px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <RefreshCw size={13} />
                </button>
              </div>
            )}

            <div ref={bottomRef} style={{ height: 20 }} />
          </div>
        )}
      </div>

      {/* Input */}
      <ChatInput
        value={input}
        onChange={setInput}
        onSubmit={() => sendMessage()}
        isLoading={isLoading || awaitingReply}
        disabled={false}
      />
    </div>
  )
}
